import { Link, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Heart, Home, MessageCircle, Plus, User } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

function useUnreadMessages(userId: string | undefined) {
  return useQuery({
    queryKey: ["unread-messages", userId],
    enabled: !!userId,
    refetchInterval: 10_000,
    queryFn: async () => {
      const { count } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .eq("recipient_id", userId!)
        .eq("read", false);
      return count ?? 0;
    },
  });
}

const leftItems = [
  { to: "/", label: "Əsas", icon: Home, match: (p: string) => p === "/" },
  {
    to: "/secilmisler",
    label: "Seçilmişlər",
    icon: Heart,
    match: (p: string) => p.startsWith("/secilmisler"),
  },
] as const;

const rightItems = [
  {
    to: "/mesajlar",
    label: "Mesajlar",
    icon: MessageCircle,
    match: (p: string) => p.startsWith("/mesajlar"),
  },
] as const;

export function BottomNav() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { user } = useAuth();
  const { data: unread = 0 } = useUnreadMessages(user?.id);

  const profileTo = user ? "/profil" : "/giris";
  const profileActive =
    pathname.startsWith("/profil") ||
    pathname.startsWith("/giris") ||
    pathname.startsWith("/qeydiyyat");

  return (
    <>
      <nav
        aria-label="Elan yarat naviqasiyası"
        className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 md:hidden"
        style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
      >
        <Link to="/yeni-elan" aria-label="Elan yarat" className="block">
          <span className="grid size-14 place-items-center rounded-full bg-info text-white shadow-glow ring-4 ring-card">
            <Plus className="size-8" strokeWidth={3} />
          </span>
        </Link>
      </nav>
      <nav
        aria-label="Alt naviqasiya"
        className="fixed inset-x-0 bottom-0 z-40 max-w-full border-t border-border bg-card/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        <ul className="grid grid-cols-5">
        {leftItems.map((it) => {
          const active = it.match(pathname);
          return (
            <li key={it.to}>
              <Link
                to={it.to}
                className={cn(
                  "flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors",
                  active ? "text-primary" : "text-muted-foreground",
                )}
              >
                <it.icon className={cn("size-5", active && "fill-primary/15")} />
                {it.label}
              </Link>
            </li>
          );
        })}
        <li aria-hidden="true" />
        {rightItems.map((it) => {
          const active = it.match(pathname);
          return (
            <li key={it.to}>
              <Link
                to={it.to}
                className={cn(
                  "flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors",
                  active ? "text-primary" : "text-muted-foreground",
                )}
              >
                <span className="relative">
                  <it.icon className={cn("size-5", active && "fill-primary/15")} />
                  {it.to === "/mesajlar" && unread > 0 && (
                    <span className="absolute -right-2 -top-1.5 grid min-w-4 place-items-center rounded-full bg-destructive px-1 text-[9px] font-bold leading-4 text-white shadow-sm">
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </span>
                {it.label}
              </Link>
            </li>
          );
        })}
        <li>
          <Link
            to={profileTo}
            className={cn(
              "flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors",
              profileActive ? "text-primary" : "text-muted-foreground",
            )}
          >
            <User className={cn("size-5", profileActive && "fill-primary/15")} />
            Profil
          </Link>
        </li>
        </ul>
      </nav>
    </>
  );
}